import React, { useLayoutEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, LogOut, ExternalLink, X } from "lucide-react";
import TenantPicker from "./TenantPicker";
import { useT } from "@/lib/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const linkClass = (top) => ({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
  isActive ? `${top ? "bg-blue-50 text-blue-600" : "bg-slate-50 text-slate-900"} shadow-sm` : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`;

const trimmed = (path) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

/** NavLink's own matching rule: exact for `end`, otherwise equal or a prefix on a segment boundary. */
const matches = (item, pathname) => {
  const to = trimmed(item.to);
  const here = trimmed(pathname);
  return here === to || (!item.end && here.startsWith(`${to}/`));
};

/** The entry the address belongs to, as `{ section, item }`; the longest matching `to` wins. */
const activeEntry = (sections, pathname) => {
  let best = null;
  for (const section of sections) {
    for (const item of section.items) {
      if (matches(item, pathname) && (!best || item.to.length > best.item.to.length)) best = { section, item };
    }
  }
  return best;
};

/**
 * The key of the section that owns `pathname` among the (access-filtered) `sections`, or `null`
 * when no entry the operator may open matches it.
 */
export const activeSectionKey = (sections, pathname) => activeEntry(sections, pathname)?.section.key ?? null;

/**
 * Scroll `scroller` the minimum distance that brings `el` into its visible box. Does nothing when
 * `el` is already fully visible, so a click on a visible item never moves the list; an element
 * taller than the box is aligned by its top. (`scrollIntoView` is not used: it also scrolls the
 * window and the main pane.)
 */
export function revealInScroller(scroller, el) {
  if (!scroller || !el) return;
  const box = scroller.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  if (rect.top < box.top || (rect.bottom > box.bottom && rect.height >= box.height)) {
    scroller.scrollTop += rect.top - box.top;
  } else if (rect.bottom > box.bottom) {
    scroller.scrollTop += rect.bottom - box.bottom;
  }
}

/**
 * The Super Admin navigation column. Declared at module scope on purpose: a component defined
 * inside the layout's render body gets a new type on every render, which remounts the subtree and
 * resets the scroll position and the practice switcher.
 *
 * `sections` is already filtered by the operator's access; `variant` is "desktop" or "mobile".
 */
export default function SuperAdminSidebar({
  sections, expandedSections, onToggleSection, isSuperAdmin, onNavigate, onTenantSwitch, onLogout, variant,
}) {
  const t = useT("superadmin");
  const tc = useT("common");
  const { pathname } = useLocation();
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);
  const active = activeEntry(sections, pathname);
  const activeOpen = active ? expandedSections[active.section.key] !== false : false;

  // After the active section has opened (deep link, reload, opening the mobile overlay, a route
  // change), bring the active item into view; a visible item is left where it is.
  useLayoutEffect(() => {
    revealInScroller(scrollerRef.current, activeRef.current);
  }, [pathname, activeOpen, variant]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-6 flex items-center justify-between sticky top-0 bg-white z-20 border-b border-slate-100 md:border-b-0">
        <Link to="/super-admin" className="flex items-center gap-3 group" onClick={onNavigate}>
          <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform">
            <div className="w-3.5 h-3.5 rounded-full bg-white blur-[0.5px]"></div>
          </div>
          <div>
            <div className="font-bold text-xl tracking-tight text-slate-800">Solunary</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{t("layout.title") || "SUPER ADMIN"}</div>
          </div>
        </Link>
        {variant === "mobile" && (
          <button onClick={onNavigate} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div ref={scrollerRef} data-testid={`sa-sidebar-scroll-${variant}`} className="px-4 pb-4 flex-1 min-h-0 overflow-y-auto" data-lenis-prevent="true">
        <div className="space-y-1">
          {sections.map((section) => {
            const links = section.items.map((item) => (
              <NavLink
                key={item.to}
                ref={active?.item === item ? activeRef : undefined}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={linkClass(item.top)}
              >
                <item.icon className="w-4 h-4" /> {t(item.labelKey)}
              </NavLink>
            ));
            if (section.key === "top") return <React.Fragment key={section.key}>{links}</React.Fragment>;
            if (!section.collapsible) {
              return (
                <div key={section.key} className="pt-4 pb-1">
                  <div className="flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                    {t(section.titleKey)}
                  </div>
                  {links}
                </div>
              );
            }
            const expanded = Boolean(expandedSections[section.key]);
            const panelId = `sa-section-${section.key}-${variant}`;
            return (
              <div key={section.key} className="pt-2 pb-1">
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => onToggleSection(section.key)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1 cursor-pointer hover:text-slate-600 transition-colors"
                >
                  <span>{t(section.titleKey)}</span>
                  {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {expanded && <div id={panelId} className="pl-3 space-y-1">{links}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-4 bg-slate-50 border-t border-slate-100">
        {/* Opening a practice's admin is a super admin act (and `/tenants/options` is super admin only). */}
        {isSuperAdmin && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 px-1">{t("layout.tenantSwitcher")}</div>
            {/* Opens upward: the switcher sits at the foot of the sidebar. */}
            <TenantPicker
              value=""
              onChange={(_id, option) => onTenantSwitch(option?.slug)}
              placeholder={t("layout.chooseTenant")}
              ariaLabel={t("layout.tenantSwitcher")}
              placement="top"
              fitWidth
              testId="sa-tenant-switcher"
            />
          </div>
        )}

        <div className="flex items-center justify-between px-1 mb-3">
          <LanguageSwitcher variant="light" />
          <Link to="/platform" className="text-[11px] text-blue-600 font-semibold flex items-center gap-1 hover:underline">
            {t("layout.linkPlatform")} <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        <button onClick={onLogout} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 bg-white border border-slate-200 shadow-sm hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 w-full transition-colors">
          <LogOut className="w-4 h-4" /> {tc("actions.logout")}
        </button>
      </div>
    </div>
  );
}
